import { create } from 'zustand/react';

import type { Dictionary } from '@/shared/legacy/contexts/LocalizationContext/types';

export type Language = 'nl' | 'en';

type I18nStore = {
  dictionary: Dictionary;
  language: Language;
  /**
   * A language owned by one surface rather than by the viewer's account.
   *
   * Candidate-facing intake pages render whatever language the *candidate's* record says.
   * That is not the viewer's preference and must never be written back as one: a recruiter
   * opening a candidate link in their own browser would otherwise have that candidate's
   * language saved to their account, because `LocalizationContext.setLanguage` also writes
   * `languageV2` and the `SELECTED_LANGUAGE` user toggle.
   *
   * So those pages set this instead, and never touch the context. Held while such a page is
   * mounted, cleared when it unmounts, and `null` everywhere else — with no override the
   * whole app behaves exactly as it did before this existed.
   */
  override: Language | null;
  setDictionary: (dictionary: Dictionary) => void;
  setLanguage: (language: Language) => void;
  setOverride: (override: Language | null) => void;
};

export const useI18nStore = create<I18nStore>((set) => ({
  dictionary: {} as unknown as Dictionary,
  language: 'nl',
  override: null,
  setDictionary: (dictionary) => set({ dictionary }),
  setLanguage: (language) => set({ language }),
  // The override is seeded from an API field, and a DTO's type is a claim rather than a check.
  // Anything that is not a language we can render is refused here, at the one entry point, so a
  // bad value cannot reach a dictionary lookup or a locale.
  setOverride: (override) => set({ override: override === 'nl' || override === 'en' ? override : null }),
}));
